import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axiosInstanceV2 from "@lhd/libs/axiosV2";
import {
  CheckInviteNotificationResponse,
  WorkerCancalation,
  WorkerInvition,
  WorkerInvitionInput,
  WorkerRejection,
} from "@lhd/types";
import { getCompanyId } from "@lhd/utility";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";

const invalidateWorkerNotificationQueries = async (queryClient: {
  invalidateQueries: (queryKey: unknown[]) => Promise<unknown>;
}) => {
  await queryClient.invalidateQueries([
    QUERY_NAME.CHECK_INVITE_NOTIFICATION,
    getCompanyId(),
  ]);
  await queryClient.invalidateQueries([QUERY_NAME.WORKER_SETTING_INVATIONS]);
  await queryClient.invalidateQueries([
    QUERY_NAME.WORKER_NOTI_REJECT_INVITATIONS,
    getCompanyId(),
  ]);
  await queryClient.invalidateQueries([
    QUERY_NAME.WORKER_NOTI_CANCELLED_INVITATIONS,
    getCompanyId(),
  ]);
};

export const useNotificationInviteCheck = ({
  enabled,
}: {
  enabled?: boolean;
}) => {
  return useQuery(
    [QUERY_NAME.CHECK_INVITE_NOTIFICATION, getCompanyId()],
    async () => {
      const res = await axiosInstanceV2({
        method: "get",
        url: `${SERVICES.CHECK_INVITE_NOTIFICATION}`,
        params: {
          company_id: getCompanyId(),
        },
      });

      return res.data as CheckInviteNotificationResponse;
    },
    {
      refetchInterval: 5 * 60 * 1000,
      enabled,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: "always",
      staleTime: 0,
      cacheTime: 0,
      retry: 0,
    },
  );
};

export const useGetWorkerInvitions = ({
  enabled,
  uuid,
}: {
  enabled?: boolean;
  uuid?: string;
}) => {
  return useQuery(
    [QUERY_NAME.WORKER_SETTING_INVATIONS, uuid],
    async () => {
      const res = await axiosInstanceV2({
        method: "get",
        url: `${SERVICES.WORKER_SETTING_INVITATIONS}`,
        params: {
          page: 1,
          per_page: 1000,
          offset: 0,
        },
      });

      return res.data as WorkerInvition[];
    },
    {
      enabled: enabled && !!uuid,
      retry: 0,
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: "always",
    },
  );
};

export const useSubmitWorkerInvitations = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: WorkerInvitionInput[]) => {
      return axiosInstanceV2({
        method: "POST",
        url: `${SERVICES.WORKER_SHARE_SETTINGS}/confirm_invitations`,
        data: {
          share_settings: data,
        },
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        void invalidateWorkerNotificationQueries(queryClient);
        notification["success"]({
          message: "保存しました。",
        });
      },
      onError() {
        notification["error"]({
          message: "保存に失敗しました。",
        });
      },
    },
  );
};

export const useGetNotiRejections = ({
  enabled,
  uuid,
}: {
  enabled?: boolean;
  uuid?: string;
}) => {
  return useQuery(
    [QUERY_NAME.WORKER_NOTI_REJECT_INVITATIONS, getCompanyId(), uuid],
    async () => {
      const res = await axiosInstanceV2({
        method: "get",
        url: `${SERVICES.WORKER_SHARE_SETTINGS}/rejections`,
        params: {
          company_id: getCompanyId(),
          page: 1,
          per_page: 1000,
          offset: 0,
        },
      });

      return res.data as WorkerRejection[];
    },
    {
      enabled: enabled && !!uuid,
      retry: 0,
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: "always",
    },
  );
};

export const useMarkAsReadRejections = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (mark_as_read_ids: number[]) => {
      return axiosInstanceV2({
        method: "POST",
        url: `${SERVICES.WORKER_SHARE_SETTINGS}/mark_as_read_rejections`,
        data: {
          company_id: getCompanyId(),
          mark_as_read_ids: mark_as_read_ids,
        },
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        void invalidateWorkerNotificationQueries(queryClient);
      },
      onError() {
        notification["error"]({
          message: "通知の既読処理に失敗しました。",
        });
      },
    },
  );
};

export const useGetNotiForWorkerCancellations = ({
  enabled,
  uuid,
}: {
  enabled?: boolean;
  uuid?: string;
}) => {
  return useQuery(
    [QUERY_NAME.WORKER_NOTI_CANCELLED_INVITATIONS, getCompanyId(), uuid],
    async () => {
      const res = await axiosInstanceV2({
        method: "get",
        url: `${SERVICES.WORKER_SHARE_SETTINGS}/cancellations`,
        params: {
          company_id: getCompanyId(),
          page: 1,
          per_page: 1000,
          offset: 0,
        },
      });

      return res.data as WorkerCancalation[];
    },
    {
      enabled: enabled && !!uuid,
      retry: 0,
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: "always",
    },
  );
};

export const useMarkAsReadCancellations = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (cancellations: { id: number; cancellation_type: string }[]) => {
      return axiosInstanceV2({
        method: "POST",
        url: `${SERVICES.WORKER_SHARE_SETTINGS}/mark_as_read_cancellations`,
        data: {
          company_id: getCompanyId(),
          cancellations: cancellations,
        },
        headers: {
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        void invalidateWorkerNotificationQueries(queryClient);
      },
      onError() {
        notification["error"]({
          message: "通知の既読処理に失敗しました。",
        });
      },
    },
  );
};
